import { useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, LinearProgress, IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import TerminalIcon from '@mui/icons-material/Terminal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import useOffensiveStore from '../../stores/offensiveStore';
import { getTacticColor, getTechniqueById } from '../../data/mitreAttack';
import techniqueExecutionService from '../../services/techniqueExecutionService';
import './TechniquePanel.css';

const TechniqueCard = ({ technique, onExecute, onStop, isExecuting }) => {
  const { setSelectedTerminal, setActiveTechnique, currentEngagement } = useOffensiveStore();
  const techniqueData = getTechniqueById(technique.techniqueId);
  const tacticColor = getTacticColor(technique.tacticId);

  const getStatusIcon = () => {
    switch (technique.status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: '#ef4444', fontSize: 20 }} />;
      case 'running':
        return <PlayArrowIcon sx={{ color: '#f59e0b', fontSize: 20 }} />;
      case 'queued':
        return <HourglassEmptyIcon sx={{ color: '#6b7280', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (technique.status) {
      case 'completed':
        return '#10b981';
      case 'failed':
        return '#ef4444';
      case 'running':
        return '#f59e0b';
      case 'queued':
        return '#6b7280';
      default:
        return '#4b5563';
    }
  };

  const handleViewTerminal = () => {
    setSelectedTerminal(technique.id);
    setActiveTechnique(technique.id);
  };

  return (
    <Card
      className={`technique-card technique-card-${technique.status}`}
      sx={{
        background: 'rgba(30, 41, 59, 0.6)',
        backdropFilter: 'blur(10px)',
        border: `1px solid ${getStatusColor()}40`,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 16px ${getStatusColor()}30`,
          border: `1px solid ${getStatusColor()}80`,
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Chip
              label={technique.techniqueId}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                background: tacticColor,
                color: '#fff',
                mb: 0.5
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                lineHeight: 1.3,
                mt: 0.5
              }}
            >
              {techniqueData?.name || technique.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getStatusIcon()}
            <Tooltip title="View Terminal Output">
              <IconButton
                size="small"
                onClick={handleViewTerminal}
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  '&:hover': {
                    color: '#fff',
                    background: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <TerminalIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Tool and Command */}
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.7rem',
              display: 'block',
              mb: 0.3
            }}
          >
            Tool: <span style={{ color: tacticColor, fontWeight: 600 }}>{technique.tool}</span>
          </Typography>
          <Box
            sx={{
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: 1,
              p: 1,
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              color: '#10b981',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {technique.command}
          </Box>
        </Box>

        {/* Agent */}
        {technique.agent && (
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.4)',
              fontSize: '0.65rem',
              display: 'block',
              mb: 1
            }}
          >
            Agent: {technique.agent}
          </Typography>
        )}

        {/* Progress Bar */}
        {technique.status === 'running' && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant={technique.progress > 0 ? 'determinate' : 'indeterminate'}
              value={technique.progress}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${tacticColor} 0%, ${tacticColor}CC 100%)`,
                }
              }}
            />
            {technique.progress > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.65rem',
                  mt: 0.5,
                  display: 'block'
                }}
              >
                {technique.progress}% complete
              </Typography>
            )}
          </Box>
        )}

        {/* Findings Count */}
        {technique.findings && technique.findings.length > 0 && (
          <Chip
            label={`${technique.findings.length} finding${technique.findings.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              background: '#10b981',
              color: '#fff',
              mt: 1
            }}
          />
        )}

        {/* Timing */}
        {technique.startTime && (
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.3)',
              fontSize: '0.6rem',
              display: 'block',
              mt: 1
            }}
          >
            Started: {new Date(technique.startTime).toLocaleTimeString()}
            {technique.endTime && ` • Duration: ${Math.round((new Date(technique.endTime) - new Date(technique.startTime)) / 1000)}s`}
          </Typography>
        )}

        {/* Action Buttons — hidden for PentAGI-managed techniques */}
        {technique.agent !== 'pentagi' && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
          {technique.status === 'running' ? (
            <Tooltip title="Stop Technique">
              <IconButton
                size="small"
                onClick={() => onStop(technique.id)}
                disabled={isExecuting}
                sx={{
                  color: '#ef4444',
                  border: '1px solid #ef4444',
                  '&:hover': {
                    background: 'rgba(239, 68, 68, 0.1)',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(239, 68, 68, 0.3)',
                    borderColor: 'rgba(239, 68, 68, 0.3)',
                  }
                }}
              >
                <StopIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Execute Technique">
              <IconButton
                size="small"
                onClick={() => onExecute(technique.id)}
                disabled={isExecuting || !currentEngagement}
                sx={{
                  color: '#10b981',
                  border: '1px solid #10b981',
                  '&:hover': {
                    background: 'rgba(16, 185, 129, 0.1)',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(16, 185, 129, 0.3)',
                    borderColor: 'rgba(16, 185, 129, 0.3)',
                  }
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        )}
      </CardContent>
    </Card>
  );
};

const TechniquePanel = () => {
  const { techniques, activeTactic, currentEngagement } = useOffensiveStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Filter techniques by active tactic
  const filteredTechniques = activeTactic
    ? techniques.filter(t => t.tacticId === activeTactic)
    : techniques;

  // Sort by status priority: running > queued > completed > failed
  const statusPriority = { running: 1, queued: 2, completed: 3, failed: 4 };
  const sortedTechniques = [...filteredTechniques].sort((a, b) => {
    return (statusPriority[a.status] || 5) - (statusPriority[b.status] || 5);
  });

  const handleExecuteTechnique = async (techniqueId) => {
    if (!currentEngagement?.id) {
      setError('No engagement selected');
      setSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      await techniqueExecutionService.executeTechnique(
        currentEngagement.id,
        techniqueId,
        {}
      );
    } catch (err) {
      setError(err.message);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleStopTechnique = async (techniqueId) => {
    if (!currentEngagement?.id) return;

    try {
      setLoading(true);
      await techniqueExecutionService.stopTechnique(
        currentEngagement.id,
        techniqueId
      );
    } catch (err) {
      setError(err.message);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const isExecuting = (techniqueId) => {
    return techniqueExecutionService.isExecuting(techniqueId);
  };

  return (
    <Box className="technique-panel" sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            color: '#fff',
            fontWeight: 600,
            fontSize: '1rem',
            mb: 0.5
          }}
        >
          Active Techniques
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '0.75rem'
          }}
        >
          {filteredTechniques.length} technique{filteredTechniques.length !== 1 ? 's' : ''} {activeTactic ? 'in current tactic' : 'total'}
        </Typography>
      </Box>

      {/* Techniques Grid */}
      {sortedTechniques.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', // Responsive grid
            gap: 2,
            maxHeight: 'calc(100vh - 350px)',
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(79, 70, 229, 0.5)', // Updated to indigo
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(79, 70, 229, 0.7)',
              },
            },
          }}
        >
          {sortedTechniques.map((technique) => (
            <TechniqueCard 
              key={technique.id} 
              technique={technique}
              onExecute={handleExecuteTechnique}
              onStop={handleStopTechnique}
              isExecuting={isExecuting(technique.id)}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 200,
            color: 'rgba(255, 255, 255, 0.3)',
          }}
        >
          <HourglassEmptyIcon sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="body2">
            No techniques queued yet
          </Typography>
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            Start an engagement to see techniques
          </Typography>
        </Box>
      )}

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TechniquePanel;

